/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getCollectionMetadata } from '@coursera-web/recommendations-common';
import { omit } from 'lodash';

import useRouter, { useLocation } from 'js/lib/useRouter';

import {
  clearChatSession,
  dropMessages,
  sendMessage,
  setChatSessionId,
  setChatSessionStorageKey,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import { getDeviceId } from 'bundles/ai-coach-platform/components/patterns/chat/utils/logoutModeUtils';
import {
  getChatSessionStorageKey,
  persistentChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';
import { useIsLohp } from 'bundles/collections-common/utils/useIsLohp';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import {
  ASK_ANYTHING_AI_PROMPT_ID,
  DEFAULT_COACH_CONFIG,
  INTENT_TYPE_TO_COACH_CONFIG,
} from 'bundles/search-common/components/experiments/aiSearchOnLohp/constants';
import SearchBarContent from 'bundles/search-common/components/searchbar/SearchBarContent';
import type { SearchBarWrapperConfig } from 'bundles/search-common/components/searchbar/searchBarWrapperTypes';
import { AUTOCOMPLETE_SECTION_PREFIXES, autocompletePrefixes } from 'bundles/search-common/constants/autocomplete';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import { useAutocompleteSearchConfigs } from 'bundles/search-common/hooks/useAutocompleteSearchConfigs';
import { useQacGuidedProductEnabled } from 'bundles/search-common/hooks/useQacGuidedProductEnabled';
import useWarmSession from 'bundles/search-common/hooks/useWarmSession';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import { useSearchRefresh } from 'bundles/search-common/providers/SearchRefreshContext';
import type { SearchBarRecommendationCollectionFragment } from 'bundles/search-common/queries/__generated__/SearchBarRecommendations';
import { saveRecentlySearched } from 'bundles/search-common/utils/SearchUtils';
import {
  type AutocompleteItem,
  type AutocompleteItems,
  getRecentlySearchedAutocompleteItems,
  getRecentlyViewedAutocompleteItems,
  getSearchUpdatedUrl,
  inferAutocompleteMode,
  isExactPartnerNameMatch,
} from 'bundles/search-common/utils/autocomplete';
import { shouldAllowSearchNavigation } from 'bundles/search-common/utils/emptyQueryValidation';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useTrackSearch from 'bundles/search-common/utils/useTrackSearch';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/search-common';

export type Props = {
  autocompleteId?: string;
  searchIsOpen?: boolean;
  hideMobileSearchPage?: () => void;
  collectionRecommendations?: SearchBarRecommendationCollectionFragment | null;
  searchBarConfig: SearchBarWrapperConfig;
  isChatComposer?: boolean;
  placeholder?: string;
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    width: 100%;
  `,

  /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
  searchForm: css`
    margin-bottom: 0;
    width: 100%;
  `,
};

export const buildEnterpriseItemPath = (
  itemPath: string,
  { enterpriseOrgSlug, enterpriseProgramSlug }: { enterpriseOrgSlug?: string; enterpriseProgramSlug?: string }
): string => {
  if (enterpriseOrgSlug) {
    // Strip any existing program-scoped prefix before building the org-scoped URL.
    // Recently-viewed items are stored at click time, which may be a program path
    // (/programs/{slug}/learn/...). When the user is in the org-scoped home experiment
    // we need to redirect to the org-scoped equivalent instead.
    let contentPath = itemPath;
    const programPrefixMatch = itemPath.match(/^\/programs\/[^/]+(\/.*)/);
    if (programPrefixMatch?.[1]) {
      contentPath = programPrefixMatch[1];
    }
    // Degrees always stay on consumer — never redirect to org-scoped XDP.
    if (contentPath.startsWith('/degrees/')) {
      return itemPath;
    }
    if (!contentPath.startsWith('/organizations/')) {
      return `/organizations/${enterpriseOrgSlug}${contentPath}?source=search`;
    }
    return contentPath;
  }
  if (enterpriseProgramSlug && !itemPath.startsWith('/programs/')) {
    return `/programs/${enterpriseProgramSlug}${itemPath}?source=search`;
  }
  return itemPath;
};

const SearchBarContentWrapperContent = (props: Props) => {
  const { hideMobileSearchPage, searchIsOpen, searchBarConfig, isChatComposer, placeholder } = props;
  const { enterpriseSearchBarConfig } = searchBarConfig;
  const router = useRouter();
  const { searchRefreshKey } = useSearchRefresh() || {};

  const currentQueryParams = router.location?.query;
  // inferQueryFromParams handles string | string[] | null | undefined safely
  const currentQueryString = inferQueryFromParams(currentQueryParams?.query);
  const isConsumerSearchPage = useIsConsumerSerp();
  const isLohpForRecentSearches = useIsLohp();
  const [searchText, setSearchText] = useState<string>(currentQueryString);
  const [refreshKey, setRefreshKey] = useState(0);
  // Recompute items when refreshKey changes to pick up localStorage updates
  const recentlySearchedItems = React.useMemo(() => {
    // refreshKey is intentionally used to force recomputation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = [refreshKey, searchRefreshKey];
    const items = getRecentlySearchedAutocompleteItems();
    // Only show AI prompt recent searches on LOHP and consumer search pages
    if (!isLohpForRecentSearches && !isConsumerSearchPage) {
      return items.filter((item) => !item.isAiPrompt);
    }
    return items;
  }, [refreshKey, isLohpForRecentSearches, isConsumerSearchPage, searchRefreshKey]);

  const recentlyViewedItems = React.useMemo(() => {
    // refreshKey is intentionally used to force recomputation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = refreshKey;
    return getRecentlyViewedAutocompleteItems(props.collectionRecommendations);
  }, [refreshKey, props.collectionRecommendations]);

  const recommendationsExplainer = React.useMemo(() => {
    const collection = props.collectionRecommendations;
    if (!collection?.id) {
      return undefined;
    }
    return getCollectionMetadata({
      __typename: 'Recommendation_Collection',
      id: collection.id,
      additionalInput: collection.additionalInput,
      programs: collection.programs,
      recommenderConfigurationHash: collection.recommenderConfigurationHash,
      context: collection.context,
    }).recommendationsExplainer;
  }, [props.collectionRecommendations]);

  const [autocompleteMode, setAutocompleteMode] = useState<AutocompleteMode>(() =>
    inferAutocompleteMode(
      searchText,
      getRecentlySearchedAutocompleteItems(),
      getRecentlyViewedAutocompleteItems(props.collectionRecommendations)
    )
  );
  const trackSearch = useTrackSearch();
  const location = useLocation();
  const { coachOpen, setCoachOpen, setContextConfig } = useCoachState();
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));
  const isCoachBusy = isMessageLoading || isMessageStreaming;
  const referrer = location.search ? `${location.pathname}${location.search}` : location.pathname;
  const { onboardingUrl } = useBuildOnboardingUrl({ referrer });

  const searchConfigs = useAutocompleteSearchConfigs(enterpriseSearchBarConfig);
  const isQacGuidedProductEnabled = useQacGuidedProductEnabled(enterpriseSearchBarConfig, searchText.length >= 3);
  const warmSession = useWarmSession();

  useEffect(() => {
    warmSession();
  }, [warmSession]);

  const doCoachSend = useCallback<(message: string, config: typeof DEFAULT_COACH_CONFIG) => void>(
    (message: string, config: typeof DEFAULT_COACH_CONFIG) => {
      const storageKey = getChatSessionStorageKey(location);
      setChatSessionStorageKey(storageKey);
      setChatSessionId(persistentChatSessionId.get(storageKey));
      sendMessage({
        input: {
          message,
          config,
        },
        enableTextStreaming: true,
        deviceId: getDeviceId(),
      });
    },
    [location]
  );

  // Stores a message to be sent once the coach panel finishes opening.
  // Avoids an arbitrary setTimeout by reacting to the coachOpen state transition.
  const pendingCoachMessage = useRef<{ message: string; config: typeof DEFAULT_COACH_CONFIG } | null>(null);

  useEffect(() => {
    if (coachOpen && pendingCoachMessage.current) {
      const pending = pendingCoachMessage.current;
      pendingCoachMessage.current = null;
      doCoachSend(pending.message, pending.config);
    }
  }, [coachOpen, doCoachSend]);

  const openCoachWithMessage = useCallback<(message: string, intentType?: string, newSession?: boolean) => void>(
    (message: string, intentType?: string, newSession = false) => {
      const config = (intentType && INTENT_TYPE_TO_COACH_CONFIG[intentType]) || DEFAULT_COACH_CONFIG;
      setContextConfig({ config });

      if (newSession) {
        dropMessages();
        clearChatSession();
      }

      if (!coachOpen) {
        pendingCoachMessage.current = { message, config };
        setCoachOpen(true);
      } else {
        doCoachSend(message, config);
      }
    },
    [coachOpen, setCoachOpen, setContextConfig, doCoachSend]
  );

  const updateUrl = (searchQuery: string) => {
    let updatedQuery = searchQuery;
    const newQueryParams: Record<string, string> = {};
    // partner matches should set a filter value instead of a query
    if (isExactPartnerNameMatch(searchQuery)) {
      // add partner filter
      newQueryParams.partners = searchQuery;
      // remove query text
      updatedQuery = '';
    }

    if (enterpriseSearchBarConfig?.searchResultsBasePath) {
      const params = new URLSearchParams({ query: updatedQuery, ...newQueryParams });
      const targetUrl = `${enterpriseSearchBarConfig.searchResultsBasePath}?${params}`;
      // Use SPA navigation so query updates don't trigger a full page reload.
      // Falls back to window.location.assign only when the target is a cross-app route.
      if (router.location?.pathname === enterpriseSearchBarConfig.searchResultsBasePath) {
        router.push(targetUrl);
      } else {
        window.location.assign(targetUrl);
      }
      return;
    }

    const cleanedQueryParams = omit(currentQueryParams, ['page', 'sortBy']);
    const searchUrl = getSearchUpdatedUrl(updatedQuery, { ...cleanedQueryParams, ...newQueryParams });
    if (isConsumerSearchPage) {
      const legacySearchUrl = getSearchUpdatedUrl(updatedQuery, newQueryParams);
      router.push(legacySearchUrl);
    } else {
      // inter-app routing does not currently work so resorting to global builtin
      window.location.assign(searchUrl);
    }
  };

  // fired on selection AND native form submit
  const handleAutocompleteSubmit = (
    rawQuery: string,
    mergedItemsForTracking?: AutocompleteItems,
    selectedItem?: AutocompleteItem
  ) => {
    warmSession();
    const sectionIdentifierPrefix = autocompletePrefixes.find((prefix) => rawQuery.startsWith(prefix));
    const queryValue = sectionIdentifierPrefix ? rawQuery.replace(sectionIdentifierPrefix, '') : rawQuery;

    const isAiPromptSelection =
      sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_ZERO_STATE ||
      sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_AS_YOU_TYPE ||
      (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.RECENT_SEARCHES && selectedItem?.isAiPrompt);

    if (isAiPromptSelection) {
      trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems, {
        suggestionIndex: selectedItem?.suggestionIndex,
        selectedItem,
      });
      hideMobileSearchPage?.();

      if (rawQuery === ASK_ANYTHING_AI_PROMPT_ID || selectedItem?.routeType === 'CHAT_ONLY') {
        if (isCoachBusy) return;
        if (rawQuery !== ASK_ANYTHING_AI_PROMPT_ID && selectedItem?.name) {
          saveRecentlySearched(selectedItem.name, {
            isAiPrompt: true,
            routeType: selectedItem.routeType,
            intentType: selectedItem.intentType,
          });
          setRefreshKey((prev) => prev + 1);
        }
        setSearchText('');

        const isAskAnything = rawQuery === ASK_ANYTHING_AI_PROMPT_ID;
        const chatSessionId = persistentChatSessionId.get(getChatSessionStorageKey(location, ''));
        const askAnythingMessage = chatSessionId ? _t('I have another question.') : _t('I have a question.');
        const message = isAskAnything ? askAnythingMessage : (selectedItem?.name ?? '');

        openCoachWithMessage(message, selectedItem?.intentType);
        return;
      }

      if (selectedItem?.routeType === 'SERP') {
        const searchQuery = selectedItem.routeParameter ?? queryValue;
        saveRecentlySearched(selectedItem.name ?? searchQuery, {
          isAiPrompt: true,
          routeType: selectedItem.routeType,
          intentType: selectedItem.intentType,
        });
        setRefreshKey((prev) => prev + 1);
        updateUrl(searchQuery);
        return;
      }

      updateUrl(queryValue);
      return;
    }

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.ENTERPRISE_SKILL && selectedItem?.path) {
      trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems, {
        suggestionIndex: selectedItem?.suggestionIndex,
        skillId: selectedItem?.eventProductId,
        skillName: selectedItem?.name,
      });
      hideMobileSearchPage?.();
      window.location.assign(selectedItem.path);
      return;
    }

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.ONBOARDING && onboardingUrl) {
      trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems, {
        suggestionIndex: selectedItem?.suggestionIndex,
      });
      hideMobileSearchPage?.();
      window.location.assign(onboardingUrl);
      return;
    }

    // Check if navigation should be allowed for this query
    if (!shouldAllowSearchNavigation(queryValue, currentQueryParams)) {
      // Prevent navigation for organic empty searches
      return;
    }

    // Only track searches that will actually execute
    // Use mergedItemsForTracking if provided (includes recently viewed + trending products from zero state)
    trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems, {
      suggestionIndex: selectedItem?.suggestionIndex,
    });

    // Hide mobile search page since search is proceeding
    hideMobileSearchPage?.();

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.RECENTLY_VIEWED) {
      // Search in mergedItemsForTracking first (includes trending products), then fall back to recentlyViewedItems
      const itemsToSearch = mergedItemsForTracking || recentlyViewedItems;
      const recentlyViewedItemMatch = itemsToSearch.find((item) => item.name === queryValue);
      if (recentlyViewedItemMatch?.path) {
        const { enterpriseProgramSlug, enterpriseOrgSlug } = enterpriseSearchBarConfig || {};
        const targetPath = buildEnterpriseItemPath(recentlyViewedItemMatch.path, {
          enterpriseOrgSlug,
          enterpriseProgramSlug,
        });
        window?.location.assign(targetPath);
        return;
      }
    }

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES) {
      // For zero state trending products, search in mergedItemsForTracking first (includes merged trending products),
      // then fall back to recentlyViewedItems
      // This ensures we find trending products that were merged into LEARNER_FAVORITES or RECENTLY_VIEWED groups
      const itemsToSearch = mergedItemsForTracking || recentlyViewedItems;
      const directMatchItem = itemsToSearch.find((item) => item.name === queryValue || item.textValue === queryValue);
      if (directMatchItem?.path) {
        const { enterpriseProgramSlug, enterpriseOrgSlug } = enterpriseSearchBarConfig || {};
        const targetPath = buildEnterpriseItemPath(directMatchItem.path, {
          enterpriseOrgSlug,
          enterpriseProgramSlug,
        });
        window?.location.assign(targetPath);
        return;
      }
    }

    // For all other selections (trending, as you type, etc.), save and navigate to search
    // save to local storage
    saveRecentlySearched(queryValue);
    // Refresh recently searched items to reflect the new search
    setRefreshKey((prev) => prev + 1);
    updateUrl(queryValue);
  };

  const handleNativeFormSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    handleAutocompleteSubmit(searchText);
  };

  const handleInputChange = (query: string) => {
    setSearchText(query);
    setAutocompleteMode(inferAutocompleteMode(query, recentlySearchedItems, recentlyViewedItems));
  };

  // ensure the search text is updated when the url changes (e.g., suggested search click on the SERP)
  React.useEffect(() => {
    setSearchText(currentQueryString);
  }, [currentQueryString]);

  // ensure the menu state is updated when the recently viewed data loads
  React.useEffect(() => {
    setAutocompleteMode(inferAutocompleteMode(searchText, recentlySearchedItems, recentlyViewedItems));
  }, [recentlySearchedItems, recentlyViewedItems, searchText]);

  const handleRefreshItems = React.useCallback<() => void>(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Shared subtree — kept in a variable to avoid duplicating JSX across branches.
  const searchContent = (
    <SearchProvider
      searchConfigs={searchConfigs}
      ssr={false}
      fetchPolicy="no-cache"
      disableBatching
      // Zero-state autocomplete doesn't consume these results (see createZeroStateAutocompleteItems).
      skipEmptyQuery
    >
      <form className="search-form" role="search" onSubmit={handleNativeFormSubmit} css={styles.searchForm}>
        <SearchBarContent
          {...props}
          searchText={searchText}
          onSubmit={handleAutocompleteSubmit}
          autocompleteMode={autocompleteMode}
          onInputChange={handleInputChange}
          recentlySearchedItems={recentlySearchedItems}
          recentlyViewedItems={recentlyViewedItems}
          recommendationsExplainer={recommendationsExplainer}
          onRefreshItems={handleRefreshItems}
          searchIsOpen={searchIsOpen ?? false}
          isChatComposer={isChatComposer}
          placeholder={placeholder || enterpriseSearchBarConfig?.placeholder}
          disableTrendingProducts={!!enterpriseSearchBarConfig}
          isQacGuidedProductEnabled={isQacGuidedProductEnabled}
          enterpriseSearchBarConfig={enterpriseSearchBarConfig}
        />
      </form>
    </SearchProvider>
  );

  return (
    <div className="rc-SearchBarContentWrapper" css={styles.wrapper}>
      {searchContent}
    </div>
  );
};

export default SearchBarContentWrapperContent;
